/* ============================================================
   JUSTYOL REDESIGN — Collection / listing system (shared)
   - sticky offset under the redesigned header (--jy-header-h)
   - filter + sort bottom sheets (mobile / tablet)
   - sort select pill (desktop) + quick chips → URL params (full reload)
   - desktop sidebar filters: auto-submit on change
   - mobile infinite scroll (24/page accumulating, IO + rect fallback)
   Filtering is URL-param based (Shopify storefront filtering params),
   full page reloads — robust, no AJAX facets.
   ============================================================ */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 767px)');

  /* ---------- helpers ---------- */
  function navWith(mutate) {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    mutate(url.searchParams);
    navigate(url.toString(), true);
  }
  function currentParams() {
    return new URL(window.location.href).searchParams;
  }

  /* ---------- sticky offset: measure the jy header ---------- */
  function initHeaderVar() {
    var header =
      document.querySelector('.jy-hdr') ||
      document.querySelector('.shopify-section-group-header-group header') ||
      document.querySelector('header');
    if (!header) return;
    var set = function () {
      var h = header.classList.contains('jy-hdr--hidden') ? 0 : header.offsetHeight;
      document.documentElement.style.setProperty('--jy-header-h', h + 'px');
    };
    set();
    window.addEventListener('resize', set, { passive: true });
    try {
      new MutationObserver(set).observe(header, { attributes: true, attributeFilter: ['class'] });
      if (window.ResizeObserver) new ResizeObserver(set).observe(header);
    } catch (e) {
      /* noop */
    }
  }

  /* ---------- search within a long filter group (Trendyol-style) ---------- */
  function initFilterSearch(root) {
    (root || document).querySelectorAll('[data-jy-fsearch]').forEach(function (input) {
      if (input.dataset.jyFsearch) return;
      input.dataset.jyFsearch = '1';
      var group = input.closest('.jy-fgroup');
      if (!group) return;
      var opts = group.querySelectorAll('.jy-fopt');
      // Enter inside the box would submit the parent filter form — block it.
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var shown = 0;
        opts.forEach(function (opt) {
          var hit = !q || (opt.textContent || '').toLowerCase().indexOf(q) !== -1;
          opt.style.display = hit ? '' : 'none';
          if (hit) shown++;
        });
        var empty = group.querySelector('.jy-fgroup__noresults');
        if (shown === 0) {
          if (!empty) {
            empty = document.createElement('div');
            empty.className = 'jy-fgroup__noresults';
            empty.textContent = '—';
            (opts[0] && opts[0].parentNode ? opts[0].parentNode : group).appendChild(empty);
          }
          empty.hidden = false;
        } else if (empty) {
          empty.hidden = true;
        }
      });
    });
  }

  /* ---------- bottom sheets ---------- */
  function openSheet(name) {
    var sheet = document.querySelector('[data-jy-sheet="' + name + '"]');
    var scrim = document.querySelector('[data-jy-scrim="' + name + '"]');
    if (!sheet) return;
    sheet.classList.add('open');
    if (scrim) scrim.classList.add('open');
    document.documentElement.classList.add('jy-sheet-lock');
  }
  function closeSheets() {
    document.querySelectorAll('.jy-sheet.open, .jy-sheet-scrim.open').forEach(function (el) {
      el.classList.remove('open');
    });
    document.documentElement.classList.remove('jy-sheet-lock');
  }

  function initSheets(root) {
    // [data-jy-open] openers live in the swappable chip row and are bound in
    // bindChips() (idempotently) so they survive AJAX swaps.
    document.querySelectorAll('[data-jy-close]').forEach(function (btn) {
      btn.addEventListener('click', closeSheets);
    });
    document.querySelectorAll('.jy-sheet-scrim').forEach(function (scrim) {
      scrim.addEventListener('click', closeSheets);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSheets();
    });
  }

  /* ---------- client-side navigation (no full reload) ----------
     Fetches the target URL, swaps the results (grid + pager) and the active
     states of the sticky chips, pushes history, and re-inits infinite scroll.
     Used for category chips, active-filter chips, quick chips and sort. The
     filter/sort bottom-sheet forms still submit normally (URL-param reload),
     except we intercept their resulting same-origin GET via navWith() only
     where wired. Falls back to a full reload on any fetch failure. */
  var listingRoot = null; // current [data-jy-listing]
  var navToken = 0;

  function getResults() {
    return listingRoot ? listingRoot.querySelector('[data-jy-results]') : null;
  }

  function setLoading(on) {
    var res = getResults();
    if (!res) return;
    res.classList.toggle('jy-loading', !!on);
    var main = listingRoot.querySelector('.jy-coll__main') || res.parentNode;
    var spin = main.querySelector('.jy-navspin');
    if (on && !spin) {
      spin = document.createElement('div');
      spin.className = 'jy-navspin';
      spin.setAttribute('aria-hidden', 'true');
      main.appendChild(spin);
    } else if (!on && spin) {
      spin.remove();
    }
  }

  function fullReload(url) {
    window.location.assign(url);
  }

  /* swap the active-state classes on the sticky chip rows from the response */
  function swapChipStates(doc) {
    ['[data-jy-subcats]', '[data-jy-quickbar]', '[data-jy-toolbar]'].forEach(function (sel) {
      var cur = listingRoot.querySelector(sel);
      var next = doc.querySelector(sel);
      if (cur && next) cur.replaceWith(document.importNode(next, true));
    });
  }

  function navigate(url, push) {
    if (!listingRoot) return fullReload(url);
    var myToken = ++navToken;
    setLoading(true);
    fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then(function (html) {
        if (myToken !== navToken) return; // a newer navigation superseded this
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextResults = doc.querySelector('[data-jy-results]');
        var nextRoot = doc.querySelector('[data-jy-listing]');
        var curResults = getResults();
        if (!nextResults || !curResults) return fullReload(url);

        // swap grid + pager
        curResults.innerHTML = nextResults.innerHTML;
        // carry over pagination data attrs for infinite scroll
        if (nextRoot) {
          ['data-page', 'data-pages', 'data-per', 'data-total', 'data-sort'].forEach(function (a) {
            if (nextRoot.hasAttribute(a)) listingRoot.setAttribute(a, nextRoot.getAttribute(a));
          });
        }
        // swap the active states on the sticky chip rows
        swapChipStates(doc);

        if (push) history.pushState({ jyNav: true }, '', url);

        setLoading(false);

        // re-bind the freshly-swapped chips + sort select + re-init infinite scroll
        bindChips(listingRoot);
        initSort(listingRoot);
        initInfiniteScroll(listingRoot);

        // scroll the grid into view just under the sticky bar
        scrollResultsIntoView();
      })
      .catch(function () {
        if (myToken !== navToken) return;
        fullReload(url);
      });
  }

  function scrollResultsIntoView() {
    var res = getResults();
    if (!res) return;
    var sticky = listingRoot.querySelector('[data-jy-sticky]');
    var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--jy-header-h')) || 0;
    var stickyH = sticky ? sticky.offsetHeight : 0;
    var top = window.pageYOffset + res.getBoundingClientRect().top - headerH - stickyH - 8;
    if (res.getBoundingClientRect().top < headerH + stickyH) {
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  function onPopState() {
    navigate(window.location.href, false);
  }

  /* ---------- sorting ---------- */
  function applySort(value, toggle) {
    navWith(function (sp) {
      var cur = sp.get('sort_by') || '';
      if (toggle && cur === value) sp.delete('sort_by');
      else sp.set('sort_by', value);
    });
  }

  function initSort(root) {
    var select = root.querySelector('[data-jy-sortselect]');
    if (select && !select.hasAttribute('data-jy-bound')) {
      select.setAttribute('data-jy-bound', '1');
      select.addEventListener('change', function () {
        applySort(select.value, false);
      });
    }
    // sort sheet options (radio list) — close the sheet, then navigate
    document.querySelectorAll('[data-jy-sort]').forEach(function (btn) {
      if (btn.hasAttribute('data-jy-bound')) return;
      btn.setAttribute('data-jy-bound', '1');
      btn.addEventListener('click', function () {
        closeSheets();
        applySort(btn.getAttribute('data-jy-sort'), false);
      });
    });
  }

  /* ---------- sticky chip rows: category links, active-filter chips,
     quick price chips and sort chips. Re-runnable after an AJAX swap;
     uses a data-jy-bound marker so re-binding is idempotent. ---------- */
  function bindChips(root) {
    var sp = currentParams();
    var curSort = sp.get('sort_by') || '';

    // (re)bind the Filtres / sort opener pills (they live in the swapped row)
    root.querySelectorAll('[data-jy-open]').forEach(function (btn) {
      if (btn.hasAttribute('data-jy-bound')) return;
      btn.setAttribute('data-jy-bound', '1');
      btn.addEventListener('click', function () {
        openSheet(btn.getAttribute('data-jy-open'));
      });
    });

    // category chips (links) → AJAX navigate
    root.querySelectorAll('[data-jy-catchip]').forEach(function (a) {
      if (a.hasAttribute('data-jy-bound')) return;
      a.setAttribute('data-jy-bound', '1');
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;
        e.preventDefault();
        navigate(new URL(href, window.location.href).toString(), true);
      });
    });

    // active-filter removal chips (links with url_to_remove) → AJAX navigate
    root.querySelectorAll('.jy-afchip').forEach(function (a) {
      if (a.hasAttribute('data-jy-bound')) return;
      a.setAttribute('data-jy-bound', '1');
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        if (!href) return;
        e.preventDefault();
        navigate(new URL(href, window.location.href).toString(), true);
      });
    });

    // sort chips (Nouveautés / Top ventes / Promotions fallback)
    root.querySelectorAll('[data-jy-sort-chip]').forEach(function (chip) {
      var v = chip.getAttribute('data-jy-sort-chip');
      if (curSort === v) chip.classList.add('jy-chip--active');
      if (chip.hasAttribute('data-jy-bound')) return;
      chip.setAttribute('data-jy-bound', '1');
      chip.addEventListener('click', function () {
        applySort(v, true);
      });
    });

    // quick price-param chips (Livraison gratuite, < 200 Dhs)
    root.querySelectorAll('[data-jy-param]').forEach(function (chip) {
      var param = chip.getAttribute('data-jy-param');
      var value = chip.getAttribute('data-jy-value');
      if (sp.get(param) === value) chip.classList.add('jy-chip--active');
      if (chip.hasAttribute('data-jy-bound')) return;
      chip.setAttribute('data-jy-bound', '1');
      chip.addEventListener('click', function () {
        navWith(function (q) {
          if (q.get(param) === value) q.delete(param);
          else q.set(param, value);
        });
      });
    });
  }

  /* ---------- filter forms (sheet + desktop sidebar) ---------- */
  function initFilterForms(root) {
    var forms = [];
    document.querySelectorAll('.jy-sheet--filter form, [data-jy-autosubmit]').forEach(function (f) {
      if (forms.indexOf(f) === -1) forms.push(f);
    });
    forms.forEach(function (form) {
      // drop empty inputs so URLs stay clean
      form.addEventListener('submit', function () {
        form.querySelectorAll('input[type="number"], input[type="search"], input[type="text"]').forEach(function (input) {
          if (!input.value) input.disabled = true;
        });
      });
      // price quick-range chips set the max input
      form.querySelectorAll('[data-jy-pricemax]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          var group = chip.closest('[data-jy-pricegroup]') || form;
          var maxInput = group.querySelector('[data-jy-pricemax-input]');
          group.querySelectorAll('[data-jy-pricemax]').forEach(function (c) {
            c.classList.toggle('jy-chip--active', c === chip);
          });
          if (maxInput) maxInput.value = chip.getAttribute('data-jy-pricemax') || '';
          if (form.hasAttribute('data-jy-autosubmit')) submitForm(form);
          // setting .value via JS doesn't fire change — dispatch it so the
          // sheet's live count refreshes
          else if (maxInput) maxInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      // desktop sidebar: instant apply on checkbox change
      if (form.hasAttribute('data-jy-autosubmit')) {
        form.addEventListener('change', function (e) {
          if (e.target && e.target.type === 'checkbox') submitForm(form);
        });
      } else {
        // mobile sheet: live "Apply (N)" preview as options change
        var applyBtn = form.querySelector('[data-jy-apply]');
        var countEl = applyBtn && applyBtn.querySelector('[data-jy-apply-count]');
        if (countEl) {
          var token = 0;
          var timer = null;
          var sectionEl = form.closest('.shopify-section');
          var sid = sectionEl ? sectionEl.id.replace(/^shopify-section-/, '') : '';
          var refresh = function () {
            var mine = ++token;
            var sp = new URLSearchParams();
            new FormData(form).forEach(function (v, k) {
              if (v !== '' && v != null) sp.append(k, v);
            });
            // fetch only this section (lighter than the whole page) to read the count
            if (sid) sp.append('section_id', sid);
            var url = form.getAttribute('action') + '?' + sp.toString();
            countEl.textContent = ' …';
            fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
              .then(function (r) {
                return r.text();
              })
              .then(function (html) {
                if (mine !== token) return; // a newer change superseded this
                var listing = new DOMParser()
                  .parseFromString(html, 'text/html')
                  .querySelector('[data-jy-listing]');
                var total = listing && listing.getAttribute('data-total');
                countEl.textContent = total != null && total !== '' ? ' (' + total + ')' : '';
              })
              .catch(function () {
                if (mine === token) countEl.textContent = '';
              });
          };
          form.addEventListener('change', function () {
            clearTimeout(timer);
            timer = setTimeout(refresh, 300);
          });
        }
      }
    });
  }
  function submitForm(form) {
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  /* ---------- mobile infinite scroll ---------- */
  var iscrollCleanup = null;
  function initInfiniteScroll(root) {
    // tear down a previous instance (re-init happens after an AJAX swap)
    if (iscrollCleanup) {
      iscrollCleanup();
      iscrollCleanup = null;
    }
    var grid = root.querySelector('[data-jy-grid]');
    var sentinel = root.querySelector('[data-jy-sentinel]');
    if (!grid || !sentinel) return;

    var page = parseInt(root.getAttribute('data-page'), 10) || 1;
    var pages = parseInt(root.getAttribute('data-pages'), 10) || 1;
    var total = parseInt(root.getAttribute('data-total'), 10) || 0;
    var progress = root.querySelector('[data-jy-progress]');
    var info = root.querySelector('[data-jy-pageinfo]');
    var sep = info ? info.getAttribute('data-sep') || ' sur ' : ' sur ';
    var loading = false;
    var done = page >= pages;

    function updateProgress() {
      if (!progress) return;
      var loaded = grid.querySelectorAll('.jy-pcard').length;
      progress.textContent = loaded + sep + total;
    }

    function finish() {
      done = true;
      sentinel.remove();
      cleanup();
    }

    function loadMore() {
      if (loading || done || !MOBILE.matches) return;
      loading = true;
      var url = new URL(window.location.href);
      url.searchParams.set('page', String(page + 1));
      /* fetch only the listing section (Section Rendering API) instead of the
         full page layout — much smaller payload per infinite-scroll page */
      var sid = root.getAttribute('data-section-id');
      if (sid) url.searchParams.set('section_id', sid);
      fetch(url.toString(), { headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.querySelector('[data-jy-grid]');
          if (!nextGrid) return finish();
          var frag = document.createDocumentFragment();
          Array.prototype.slice.call(nextGrid.children).forEach(function (child) {
            frag.appendChild(document.importNode(child, true));
          });
          grid.appendChild(frag);
          page += 1;
          root.setAttribute('data-page', String(page));
          updateProgress();
          if (page >= pages) finish();
        })
        .catch(function () {
          /* network hiccup: allow retry on next intersection */
        })
        .then(function () {
          loading = false;
        });
    }

    /* primary: IntersectionObserver */
    var io = null;
    try {
      io = new IntersectionObserver(
        function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) loadMore();
        },
        { rootMargin: '700px' }
      );
      io.observe(sentinel);
    } catch (e) {
      /* noop */
    }

    /* fallback: rect check on scroll + interval (some embeds never fire IO) */
    function check() {
      if (done || loading || !sentinel.isConnected) return;
      var r = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh + 700 && r.bottom > -700) loadMore();
    }
    var iv = setInterval(check, 500);
    window.addEventListener('scroll', check, { passive: true });

    function cleanup() {
      if (io) io.disconnect();
      clearInterval(iv);
      window.removeEventListener('scroll', check);
    }
    iscrollCleanup = cleanup;

    updateProgress();
    check();
  }

  /* ---------- vendor "Suivre" (on-device only, no follower count) ---------- */
  var FOLLOW_KEY = 'jy_following';
  function getFollowing() {
    try {
      return JSON.parse(localStorage.getItem(FOLLOW_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }
  function setFollowing(arr) {
    try {
      localStorage.setItem(FOLLOW_KEY, JSON.stringify(arr));
    } catch (e) {
      /* ignore quota / private mode */
    }
  }
  function paintFollow(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = btn.querySelector('[data-jy-follow-label]');
    if (label) {
      var t = on ? btn.getAttribute('data-label-following') : btn.getAttribute('data-label-follow');
      if (t) label.textContent = t;
    }
  }
  function initFollow(root) {
    var btn = root.querySelector('[data-jy-follow]');
    if (!btn) return;
    var handle = btn.getAttribute('data-jy-follow');
    paintFollow(btn, getFollowing().indexOf(handle) !== -1);
    btn.addEventListener('click', function () {
      var cur = getFollowing();
      var i = cur.indexOf(handle);
      if (i === -1) cur.push(handle);
      else cur.splice(i, 1);
      setFollowing(cur);
      paintFollow(btn, i === -1);
    });
  }

  function init() {
    var root = document.querySelector('[data-jy-listing]');
    initHeaderVar();
    if (!root) return;
    listingRoot = root;
    initSheets(root);
    initSort(root);
    bindChips(root);
    initFilterForms(root);
    initFilterSearch(root);
    initInfiniteScroll(root);
    initFollow(root);

    // back/forward → re-fetch and swap without pushing a new entry
    window.addEventListener('popstate', onPopState);

    /* contract with dept pages: a "Filtres" link navigates here with
       #jy-filters — open the filter sheet on arrival */
    if (window.location.hash === '#jy-filters') {
      openSheet('filter');
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
